/**
 * Cross-focus deep links.
 *
 * UI only mounts `/<focus>/*` while the user's current focus matches, and switching
 * focus lands on that focus's dashboard. To open a specific page in another focus, stash
 * the target before switching; the destination focus's index route consumes it on mount.
 */

const STORAGE_KEY = 'yeshuman.pending-focus-path'
const MAX_AGE_MS = 30_000

type PendingFocusPath = { focus: string; path: string; at: number }

function storage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null
  }
}

export function stashPendingFocusPath(focus: string, path: string): void {
  const store = storage()
  if (!store) return
  const entry: PendingFocusPath = { focus, path, at: Date.now() }
  store.setItem(STORAGE_KEY, JSON.stringify(entry))
}

/** Returns and clears the stashed path for `focus`, or null when none is pending or it is stale. */
export function consumePendingFocusPath(focus: string): string | null {
  const store = storage()
  if (!store) return null
  const raw = store.getItem(STORAGE_KEY)
  if (!raw) return null
  store.removeItem(STORAGE_KEY)
  try {
    const entry = JSON.parse(raw) as Partial<PendingFocusPath>
    if (entry.focus !== focus || typeof entry.path !== 'string') return null
    if (typeof entry.at !== 'number' || Date.now() - entry.at > MAX_AGE_MS) return null
    return entry.path
  } catch {
    return null
  }
}
